/**
 * Send a signed-out browser straight to the ClaimShield sign-in page.
 *
 * Apps that sign in through the identity provider used to stop on an app-side
 * "Continue with ClaimShield" card first — one extra click that asked nothing
 * (Ahmed's walk, 2026-10-22). The redirect now happens on arrival, and the card
 * stays only as the fallback when something needs saying.
 *
 * The loop breaker: if the app sent this tab to sign-in a moment ago and the
 * tab is back on the sign-in route already, the round trip did not produce a
 * session (blocked cookie, a callback refusal), so redirecting again would spin
 * forever. The caller shows its card instead. Storage that cannot be read
 * counts as "just tried" for the same reason.
 */
const KEY = 'cs-sign-in-redirect-at'
const WINDOW_MS = 20_000

export function redirectToSignInOnce(url: string, now: number = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(KEY) || 0)
    if (now - last < WINDOW_MS) return false
    sessionStorage.setItem(KEY, String(now))
  } catch {
    return false
  }
  window.location.assign(url)
  return true
}

/** Called once a session is confirmed, so the next sign-out starts clean. */
export function clearSignInRedirect(): void {
  try { sessionStorage.removeItem(KEY) } catch { /* nothing to clear */ }
}

/**
 * Set while a sign-out is on its way to the sign-in server. Requests still in
 * flight come back 401 once the app session is revoked, and the API client
 * answers a 401 by sending the tab to /login, which now goes straight to
 * sign-in: that navigation overtook the sign-out redirect, so the person
 * skipped "You're signed out" (developer walk, 2026-10-23). Page-lifetime state:
 * the sign-out ends in a full navigation either way.
 */
let signingOut = false
export function markSigningOut(): void { signingOut = true }
export function isSigningOut(): boolean { return signingOut }
